import { Button, Layout, Modal, notification, Table, Tag, Typography } from 'antd';
import Navbar from '../../Organisms/Navbar/Navbar';
import { permissionProfileGestion, taskStateItems, truncateStringEllipsis, userSubMenu } from '../../../utils/functions';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks';
import './ReportsTemplate.less';
import { ReactComponent as Document } from '../../../res/svg/document.svg';
import { ReactComponent as TableExpand } from '../../../res/svg/table-expand.svg';
import { ReactComponent as TableHide } from '../../../res/svg/table-hide.svg';
import { ReactComponent as Exclamation } from '../../../res/svg/exclamation.svg';
import ReportsFilters from '../../Molecules/ReportsFilters/ReportsFilters';
import { ColumnsType } from 'antd/lib/table';
import Task from '../../../models/Task';
import moment from 'moment';
import AvatarStateUser from '../../Molecules/AvatarStateUser/AvatarStateUser';
import { useEffect, useMemo, useState } from 'react';
import {
  getReportTasks,
  selectTasks,
  setReportsFilter,
  unselectPoint,
  unselectTasks,
} from '../../../store/slice/reportsSlice';
import { ReportsPointTable } from '../../Atoms/ReportsPointRow/ReportsPointRow';
import { ReactComponent as CheckCircle } from '../../../res/svg/check-circle-tone.svg';
import { ReactComponent as CrossCircle } from '../../../res/svg/cross-circle.svg';
import ReportsTableContent from '../../Molecules/ReportsTableContent/ReportsTableContent';
import { getNumberOfPhotos } from '../../../utils/ApiGeometryUtils';
import SectionIndicator, { subsections } from '../../Molecules/SectionIndicator/SectionIndicator';

const { Text } = Typography;

function ReportsTemplate() {

  let navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tasks = useAppSelector(state => state.reports.tasks);
  const totalItems = useAppSelector(state => state.reports.totalItems);
  const points = useAppSelector(state => state.reports.points);
  const filtros = useAppSelector(state => state.reports.filtros);
  const loading = useAppSelector(state => state.reports.loading);
  const pointsIdsSelected: Set<number> = useAppSelector(state => state.reports.pointsIdsSelected);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    //solo perfil gestion pueden ingresar
    permissionProfileGestion(navigate);
    dispatch(getReportTasks());
  }, []);

  useEffect(() => {
    dispatch(getReportTasks());
  }, [filtros]);

  const itemsCallback = (item: string) => {
    userSubMenu(item, navigate, dispatch);
  };

  const selectedTasks = useMemo(
    () => tasks.filter(t => t.geometrias!.every(g => pointsIdsSelected.has(g.id))).map(t => t.id!),
    [tasks, pointsIdsSelected],
  );

  const columns: ColumnsType<any> = [
    Table.SELECTION_COLUMN,
    Table.EXPAND_COLUMN,
    {
      title: 'Nombre de Tarea',
      dataIndex: 'titulo',
      render: (_id, row) => <Text className='title-task'
                                  title={row.titulo}>{truncateStringEllipsis(row.titulo, 32)}</Text>,
      sortDirections: ['descend', 'ascend'],
      width: 200,
    },
    {
      title: 'Tipo de tarea',
      dataIndex: 'tipoTarea',
      render: (_id, row) => (
        <Tag title={row.tipotarea.tipotarea} style={{
          color: `#${row?.tipotarea?.color}`,
          borderColor: `#${row?.tipotarea?.color}`,
          marginRight: '0px',
        }}>{truncateStringEllipsis(row.tipotarea.tipotarea, 23)}</Tag>
      ),
      width: 150,
    },
    {
      title: 'Nota de cierre',
      dataIndex: 'notaDeCierre',
      render: (_id, row) => <Text>{truncateStringEllipsis(row.notaDeCierre, 42)}</Text>,
      width: 180,
    },
    {
      title: 'Observaciones',
      dataIndex: 'observaciones',
      render: (_id, row) => <Text title={row.observaciones}>{truncateStringEllipsis(row.observaciones, 42)}</Text>,
      width: 180,
    },
    {
      title: 'Creadas por',
      dataIndex: 'createdBy',
      render: (_id, row) => <Text title={row.observaciones}>{row.usuario.nombre + ' ' + row.usuario.apellido}</Text>,
      width: 180,
    },
    {
      title: 'Asignado(s)',
      dataIndex: 'asignado',
      align: 'left',
      render: (_id, task) => (
        <>
          {
            task.tareaUsuario[0] ?
              <>
                {task.tareaUsuario![0] !== undefined && (
                  <div className={'assign_users'}>
                    <AvatarStateUser arrayUsers={task.tareaUsuario} />
                  </div>
                )}
              </> : <>-</>
          }
        </>
      ),
    },
    {
      title: 'Fecha Realización',
      render: (row) => (
        <>
          {row.fechaDesde ?
            <>
              {moment(row.fechaDesde).format('DD/MM/YYYY')} <br></br> {moment(row.fechaHasta).format('DD/MM/YYYY')}
            </> : <>-</>}
        </>
      ),
      width: 160,
    },
    {
      title: 'Fecha Cierre',
      render: (row) => (
        <>
          {row.closedAt ?
            <>
              {moment(row.closedAt).format('DD/MM/YYYY')}
            </> : <>-</>}
        </>
      ),
      width: 160,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      align: 'center',
      render: (_id, row) => (
        <Tag className={taskStateItems(row.estado).className} style={{ marginRight: '0px' }}>
          {taskStateItems(row.estado).title}
        </Tag>
      ),
      width: 110,
    },
  ];

  const maxPhotosExceeded = useMemo(
    () => getNumberOfPhotos(points) > parseInt(process.env.REACT_APP_TASK_REPORTS_MAX_PHOTOS!),
    [points],
  );

  useEffect(() => {
    if(maxPhotosExceeded){
      notification.error({
        message: 'Cantidad máxima de fotos',
        icon: <Exclamation />,
        description: 'Superaste la cantidad máxima de fotos posible para crear un Informe (.doc).',
        style: {
          borderRadius: 8
        }
      })
    }
  }, [maxPhotosExceeded])

  const PhotosTagIcon = maxPhotosExceeded ? CrossCircle : CheckCircle;

  return (
    <Layout>
      <Navbar showItems={true} subsection={'gestion'} currentScreen={'reportes'} itemsCallback={itemsCallback} />
      <SectionIndicator subsection={subsections.reportes} />
      <Layout className={'tp-reports__layout extra-height'}>
        <div className='tp-reports__top-container'>
          <ReportsFilters />
          <Button
            className={'tp-reports__generate-doc'}
            type='primary'
            shape='round'
            icon={<Document />}
            onClick={() => setModalVisible(true)}
            size={'large'}
          >
            Generar Documento
          </Button>
        </div>
        <div className={'tp-reports__selected-points-container first'}>
          <span id={'tp-reports__selected-points'}>
            Cantidad de Puntos seleccionados:
          </span>
          <Tag id={'points-tag'}>{pointsIdsSelected.size}</Tag>
          <span id={'tp-reports__selected-points'} style={{ marginLeft: 8 }}>
            Cantidad de Fotos:
          </span>
          <Tag className={'photos-tag ' + (maxPhotosExceeded ? 'photos-exceeded' : '')}>
            <PhotosTagIcon style={{ height: 12, width: 12, marginRight: 3, marginBottom: -1 }} />
            {getNumberOfPhotos(points)}
          </Tag>
        </div>
        <div className={'tp-reports__selected-points-container'}>
          <span id={'tp-reports__selected-points'}>
            Puntos seleccionados:
          </span>
          {
            points.map(point => (
              <Tag key={point.id} closable={true} onClose={() => dispatch(unselectPoint(point))}>{point.titulo}</Tag>
            ))
          }
        </div>
        <div>
          <Table
            className={'tp-reports__table'}
            columns={columns}
            dataSource={tasks}
            expandable={{
              expandedRowRender: (record: any) => (
                <div className={'tp-reports__table-point-row'}>
                  <ReportsPointTable points={record.geometrias} />
                </div>
              ),
              expandIcon: props => (
                <Button className={'tp-reports__expand-button'} onClick={(e) => props.onExpand(props.record, e as any)}
                        type={'ghost'}>
                  {props.expanded ? <TableHide /> : <TableExpand />}
                </Button>
              ),
            }}
            rowSelection={{
              type: 'checkbox',
              hideSelectAll: true,
              selectedRowKeys: selectedTasks,
              onChange: (selectedRowKeys, selectedRows, info) => {
                const newSelected = selectedRowKeys.filter(k => !selectedTasks.includes(k as number));
                const unselected = selectedTasks.filter(k => !selectedRowKeys.includes(k as number));
                dispatch(selectTasks(newSelected as number[]));
                dispatch(unselectTasks(unselected as number[]));
              },
              getCheckboxProps: (record: Task) => {
                const selected = record.geometrias!.filter(g => pointsIdsSelected.has(g.id)).length;
                return {
                  indeterminate: selected > 0 && selected < record.geometrias!.length,
                };
              },
            }}
            rowKey={(task) => task.id}
            sortDirections={['descend', 'ascend']}
            locale={{ emptyText: loading ? '...' : 'No hay tareas para mostrar.' }}
            pagination={{
              locale: { items_per_page: '/ página' },
              className: 'tp-reports__pagination',
              current: Math.max(filtros.page, 1),
              total: totalItems,
              pageSize: 10,
              onChange: (page, pageSize) => {
                dispatch(setReportsFilter({ page, pageSize }));
              },
            }}
            loading={loading}
          />
        </div>
      </Layout>
      <Modal
        className={'tp-reports__modal'}
        title={'Generar Documento'}
        visible={modalVisible}
        width={'90%'}
        footer={null}
        onCancel={() => setModalVisible(false)}
      >
        <ReportsTableContent onClose={() => setModalVisible(false)} />
      </Modal>
    </Layout>

  );
}

export default ReportsTemplate;
